// Ubicación en el repo: src/pages/Patients.jsx
// Listado de Pacientes (spec-pacientes-agenda.md, puntos 2-4 del orden
// sugerido): tabla + búsqueda + filtro por sucursal activa (RLS ya
// existente) + eliminar (soft-delete, DECISIONS.md #19) + crear/editar
// vía modal (spec 1.3) + "Ver ficha" hacia /pacientes/:id (spec 1.4).
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { listPatients, patientHasHistory, softDeletePatient, getPatientById } from '../lib/patients';
import { formatPatientName, calculateAge, formatDate } from '../lib/format';
import DeletePatientDialog from '../components/DeletePatientDialog';
import PatientFormModal from '../components/PatientFormModal';
import Spinner from '../components/Spinner';

export default function Patients() {
  const { activeBranchId, activeModule, professional, selectableBranches } = useOutletContext();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [confirmPatient, setConfirmPatient] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Paginación (DECISIONS.md, 23-ago-2026): `page` es 1-based. `handleSearchChange`
  // resetea siempre a la página 1 — sin esto, una búsqueda nueva podría dejarte
  // "parado" en una página del listado completo donde esos resultados no
  // caben, y verías "sin resultados" aunque el paciente sí exista en otra
  // página. `totalCount` viene del `count: 'exact'` de listPatients().
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Modal crear/editar: `formOpen` controla si se muestra, `editTarget`
  // trae la ficha completa (null = creando uno nuevo).
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      const { data, count } = await listPatients({ branchId: activeBranchId, search, page, pageSize: PAGE_SIZE });
      // Resguardo: si borraste el último paciente de la última página (o una
      // búsqueda nueva devuelve menos páginas que antes), no te deja colgado
      // viendo una página vacía que ya no existe — retrocede automáticamente.
      const lastPage = Math.max(1, Math.ceil((count ?? 0) / PAGE_SIZE));
      if (data.length === 0 && page > lastPage) {
        setPage(lastPage);
        return;
      }
      setPatients(data);
      setTotalCount(count);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de pacientes.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search, page]);

  useEffect(() => {
    const t = setTimeout(refresh, 300); // debounce de búsqueda
    return () => clearTimeout(t);
  }, [refresh]);

  function handleSearchChange(e) {
    setSearch(e.target.value);
    setPage(1);
  }

  async function handleDeleteClick(patient) {
    setDeleteError('');
    try {
      const hasHistory = await patientHasHistory(patient.id);

      if (!hasHistory) {
        const ok = window.confirm(
          `¿Eliminar a ${formatPatientName(patient)}? Un Admin podrá restaurarlo más adelante.`
        );
        if (ok) {
          await softDeletePatient(patient.id, professional.id);
          await refresh();
        }
        return;
      }

      if (!professional.is_admin) {
        setDeleteError(
          `${formatPatientName(patient)} tiene historial clínico asociado. Solo un Admin puede eliminarlo.`
        );
        return;
      }

      setConfirmPatient(patient);
    } catch (err) {
      setDeleteError(err.message || 'No se pudo eliminar el paciente.');
    }
  }

  async function handleConfirmDelete() {
    if (!confirmPatient) return;
    setDeleting(true);
    try {
      await softDeletePatient(confirmPatient.id, professional.id);
      setConfirmPatient(null);
      await refresh();
    } catch (err) {
      setDeleteError(err.message || 'No se pudo eliminar el paciente.');
    } finally {
      setDeleting(false);
    }
  }

  function handleNewPatient() {
    setEditTarget(null);
    setFormOpen(true);
  }

  async function handleEditClick(patient) {
    setDeleteError('');
    setEditLoading(true);
    try {
      const full = await getPatientById(patient.id);
      setEditTarget(full);
      setFormOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la ficha del paciente.');
    } finally {
      setEditLoading(false);
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Pacientes</h1>
        <div className="patients-page__toolbar-right">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre o RUT…"
            value={search}
            onChange={handleSearchChange}
          />
          <button type="button" className="button-primary" onClick={handleNewPatient}>
            + Nuevo paciente
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {deleteError && <p className="auth-error">{deleteError}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Paciente</th>
            <th>Teléfono</th>
            <th>Edad</th>
            {activeModule === 'ORL' ? (
              <>
                <th>Audiometrías</th>
                <th>Audífonos</th>
              </>
            ) : (
              <>
                <th>Recetas</th>
                <th>Compró lentes</th>
              </>
            )}
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6}><Spinner inline /></td>
            </tr>
          ) : patients.length === 0 ? (
            <tr>
              <td colSpan={6}>
                {search.trim()
                  ? 'No hay pacientes que coincidan con tu búsqueda.'
                  : 'No hay pacientes registrados en esta sucursal.'}
              </td>
            </tr>
          ) : (
            patients.map((p) => (
              <tr key={p.id}>
                <td>
                  <div>{formatPatientName(p)}</div>
                  <div className="text-muted">{p.rut || 'Sin RUT'}</div>
                </td>
                <td>{p.phone || '—'}</td>
                <td>
                  <div>{calculateAge(p.birthdate) ?? '—'}</div>
                  <div className="text-muted">Ingreso: {formatDate(p.created_at)}</div>
                </td>
                {activeModule === 'ORL' ? (
                  <>
                    <td>{p.exams_count}</td>
                    <td>{p.hearingaids_count}</td>
                  </>
                ) : (
                  <>
                    <td>{p.prescriptions_count}</td>
                    <td>
                      {p.oftsales_count === 0
                        ? 'Sin registro'
                        : p.oftsales_bought_count > 0
                          ? 'Sí'
                          : 'No'}
                    </td>
                  </>
                )}
                <td className="actions-cell">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate(`/app/pacientes/${p.id}`)}
                  >
                    Ver ficha
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    disabled={editLoading}
                    onClick={() => handleEditClick(p)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() => handleDeleteClick(p)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {!loading && totalCount > PAGE_SIZE && (
        <div className="patients-pagination">
          <button
            type="button"
            className="link-button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ← Anterior
          </button>
          <span className="patients-pagination__label">
            Página {page} de {totalPages} · {totalCount} paciente{totalCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            className="link-button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Siguiente →
          </button>
        </div>
      )}

      {confirmPatient && (
        <DeletePatientDialog
          patient={confirmPatient}
          loading={deleting}
          onCancel={() => setConfirmPatient(null)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {formOpen && (
        <PatientFormModal
          patient={editTarget}
          branches={selectableBranches}
          defaultBranchId={activeBranchId}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
